import {
  type Component,
  For,
  Show,
  createResource,
  createSignal,
} from "solid-js";

import { getChunkPreviews } from "@/api/attachments";

export interface ContextUsedDisclosureProps {
  /** UUIDs of retrieved chunks for the assistant turn. Sent as `[]`
   *  when no RAG was applied; component renders nothing in that case. */
  chunkIds: readonly string[];
}

const ContextUsedDisclosure: Component<ContextUsedDisclosureProps> = (
  props,
) => {
  const [opened, setOpened] = createSignal(false);

  // Resource source = joined ids string. Going truthy triggers the
  // fetch on first open; the joined-ids tail re-keys the resource when
  // the upstream chunk ids change (e.g. assistant turn re-hydrates).
  //
  // The fetcher splits the source string back into ids so it doesn't
  // close over `props.chunkIds` by reference — Solid schedules the
  // fetch asynchronously and the prop could theoretically change in
  // between (see `feedback_solid_create_resource_pitfalls.md`).
  const [previews] = createResource(
    () =>
      opened() && props.chunkIds.length > 0 ? props.chunkIds.join(",") : null,
    (joined) => getChunkPreviews(joined.split(",")),
  );

  return (
    <Show when={props.chunkIds.length > 0}>
      <details
        class="chat-context-used"
        onToggle={(e) =>
          setOpened((e.currentTarget as HTMLDetailsElement).open)
        }
      >
        <summary>
          context used · {props.chunkIds.length}{" "}
          {props.chunkIds.length === 1 ? "chunk" : "chunks"}
        </summary>
        <Show
          when={!previews.loading}
          fallback={<p class="chat-context-loading">loading previews…</p>}
        >
          <Show
            when={!previews.error}
            fallback={<p class="chat-error">could not load chunk previews</p>}
          >
            <ul class="chat-context-list">
              <For each={previews()?.chunks ?? []}>
                {(c) => (
                  <li>
                    <b>
                      {c.filename} · chunk {c.ord}
                    </b>
                    <pre>{c.text_preview}</pre>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </details>
    </Show>
  );
};

export default ContextUsedDisclosure;
